import { murmurHashV3 } from '@smx-ui-common/murmurhashV3';
import { SuspenseLoadingStatus } from '@smx-ui-common/suspense-loading-status';
import { ModelType } from '@smx-ui-model/types';
import { TableResourceType } from '@smx-ui-model/types';
import { WrapperTableLoaderQuery } from '@smx-ui-table/wrapper-table-loader-query';
import { Collapse, CollapseProps, Empty, Spin } from 'antd';
import React, { memo, Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRelayEnvironment } from 'react-relay';

import { drawingSetFragment$data } from '../graphql/__generated__/drawingSetFragment.graphql';
import { DrawingSetModelType } from '../model/types';
import { AppDispatch, RootState } from '../store';
import { setBuffer, setSearchResult } from '../store/drawings-slice';
import { CollapseListDrawingSetBody } from './collapse-list-drawing-set-body';
import { CollapseListDrawingSetHeader } from './collapse-list-drawing-set-header';

interface CollapseListProps {
    model: ModelType<DrawingSetModelType>;
    listFieldName: (keyof DrawingSetModelType)[];
    limit?: number;
    itemBody: (node: any, linkBody: any) => React.ReactNode;
    storeLinkBody?: React.MutableRefObject<any>;
    filterProject?: string[];
}

export const CollapseListDrawingSet = memo(
    ({ model, listFieldName = [], limit, itemBody, storeLinkBody, filterProject }: CollapseListProps) => {
        const dispatch = useDispatch<AppDispatch>();
        const environment = useRelayEnvironment();

        const currentTags = useSelector<RootState, string[]>((state) => state.drawings.currentTags);
        const currentSearchText = useSelector<RootState, string | undefined>(
            (state) => state.drawings.currentSearchText,
        );
        const isAndFilterTag = useSelector<RootState, boolean>((state) => state.drawings.isAndFilterTag);

        const [dataSource, setDataSource] = useState<TableResourceType[]>();
        const [_, setDataPageInfo] = useState<any>();
        const [isLoading, setIsLoading] = useState<boolean>(false);
        const [openCollapseKey, setOpenCollapseKey] = useState<string[]>([]);

        useEffect(() => {
            const projectId = filterProject?.[0] || 'none';
            dispatch(
                setSearchResult({
                    [projectId]: dataSource?.filter(filterDataSource)?.length || 0,
                }),
            );

            const buffer: { [key: string]: number[] } = {};

            dataSource?.forEach((item: any) => {
                if (!item?.tags) {
                    return;
                }

                const hash = murmurHashV3(item?.tags);
                buffer[hash] = item.tags;

                item.tags?.split(',').forEach((tag: string) => {
                    if (!buffer[tag]) {
                        buffer[tag] = [];
                    }
                    buffer[tag].push(hash);
                });
            });

            dispatch(setBuffer(buffer));
        }, [dataSource, currentTags, currentSearchText]);

        const variables = {
            input: {
                page: 1,
                limit: limit || 20,
                filter: { projectId: { operator: 'IN', value: filterProject?.join(',') } },
            },
        };

        const filterDataSource = (item: TableResourceType) => {
            return isAndFilterTag
                ? filterDataSourceByTag(item) && filterDataSourceBySearchText(item)
                : filterDataSourceByTag(item) || filterDataSourceBySearchText(item);
        };

        const filterDataSourceByTag = (item: TableResourceType) => {
            const drawingSet = environment
                ?.getStore()
                .getSource()
                .get(`${item.key}`) as unknown as drawingSetFragment$data;

            if (isAndFilterTag) {
                for (const currentTag of currentTags) {
                    if (!drawingSet.tags?.split(',').includes(currentTag)) {
                        return false;
                    }
                }

                return true;
            } else {
                return drawingSet.tags
                    ?.split(',')
                    .some((tag: string) => !currentTags.length || currentTags.includes(tag));
            }
        };

        const filterDataSourceBySearchText = (item: any) => {
            const drawingSet = environment
                ?.getStore()
                .getSource()
                .get(`${item.key}`) as unknown as drawingSetFragment$data;

            if (!currentSearchText) {
                return true;
            }

            const searchText = currentSearchText.toLowerCase();
            const listFieldName: (keyof drawingSetFragment$data)[] = ['name', 'tags'];
            return listFieldName.some((fieldName) =>
                (drawingSet[fieldName] as string)?.toLowerCase().includes(searchText),
            );
        };

        const items: CollapseProps['items'] = dataSource?.filter(filterDataSource).map((item: TableResourceType) => {
            return {
                key: item.key,
                label: (
                    <div>
                        <CollapseListDrawingSetHeader
                            node={item.userRef}
                            listFieldName={listFieldName}
                            storeLinkBody={storeLinkBody}
                            openCollapseKey={openCollapseKey}
                        />
                    </div>
                ),
                children: (
                    <CollapseListDrawingSetBody
                        storeLinkBody={storeLinkBody}
                        itemBody={itemBody}
                        itemUseRef={item.userRef}
                    />
                ),
            };
        });

        const onChange = (key: string | string[]) => {
            setOpenCollapseKey(key as string[]);
        };

        return (
            <>
                <Suspense
                    fallback={
                        <SuspenseLoadingStatus
                            onStart={() => {
                                setIsLoading(true);
                            }}
                            onEnd={() => {
                                setIsLoading(false);
                            }}
                        />
                    }
                >
                    <WrapperTableLoaderQuery
                        model={model}
                        variables={variables}
                        setDataSource={setDataSource}
                        setDataPageInfo={setDataPageInfo}
                        upAdditionalField={['tags']}
                    />
                </Suspense>
                <Spin spinning={isLoading}>
                    {!items?.length ? (
                        <Empty style={{ marginTop: 135 }} description={'No drawings have been added to this project'} />
                    ) : (
                        <Collapse items={items} defaultActiveKey={[]} onChange={onChange} />
                    )}
                </Spin>
            </>
        );
    },
);
